/**
=========================================================
* Call It In LLC - Courier Dashboard
=========================================================

* Company: Call It In LLC
* Website: https://www.justcallitin.com
* Author: Shaun Matthews
* Copyright: © 2025 Call It In LLC. All rights reserved.

=========================================================

* This file is part of the Call It In dashboard system.
* Any reproduction or distribution without written consent is strictly prohibited.
*/

// Dashboards
import Analytics from "layouts/dashboards/analytics";
import DriverStatus from "layouts/dashboards/DriverStatus";
import CourierDashboard from "layouts/dashboards/CourierDashboard";
import AdminAI from "pages/AdminAI";

// My Account page — authenticated user profile
import MyAccount from "pages/MyAccount";

// Administration — company-wide settings (admin only)
import CompanySettings from "pages/CompanySettings";

// Applications
import TaskCreate from "layouts/applications/task-create";
import TaskList from "layouts/applications/task-list";

// AxisSync
import CaseCreate from "layouts/applications/case-create";
import CaseList from "layouts/applications/case-list";

// Sidebar icons
import Icon from "@mui/material/Icon";

// Maps & Driver App
import LiveMap from "pages/LiveMap";
import DriverApp from "layouts/applications/driver-app";
import RoutePlanner from "pages/RoutePlanner";

import Logout from "pages/Logout";

// User management and login pages
import Employees from "pages/Employees";
import PortalAccess from "pages/PortalAccess";
import Login from "pages/Login";

// Address Book — saved vendors, hospitals, surgery centers, and delivery sites
import AddressBook from "pages/AddressBook";

import DriverSupportView from "layouts/applications/driver-support";
import DriverRecords from "layouts/applications/driver-records";

/* const routes = [
  // Profile / user block
  {
    type: "collapse",
    name: "Elizabeth Matthews",
    key: "elizabeth-matthews",
    icon: <MDAvatar src={profilePicture} alt="Elizabeth Matthews" size="sm" />,
    collapse: [
      {
        name: "My Profile",
        key: "my-profile",
        route: "/pages/profile/profile-overview",
        component: <ProfileOverview />,
      },
      {
        name: "Settings",
        key: "profile-settings",
        route: "/pages/account/settings",
        component: <Settings />,
      },
      {
        name: "Logout",
        key: "logout",
        route: "/authentication/sign-in/basic",
        component: <SignInBasic />,
      },
    ],
  },

  { type: "divider", key: "divider-0" },

  // Dashboards (with Maps)
  {
    type: "collapse",
    name: "Dashboards",
    key: "dashboards",
    icon: <Icon fontSize="small">dashboard</Icon>,
    collapse: [
      { name: "Analytics", key: "analytics", route: "/dashboards/analytics", component: <Analytics /> },
      { name: "Sales", key: "sales", route: "/dashboards/sales", component: <Sales /> },
      { name: "Courier Metrics", key: "courier-dashboard", route: "/dashboard/courier", component: <CourierDashboard /> },
      { name: "Driver Status", key: "driver-status", route: "/dashboards/driver-status", component: <DriverStatus /> },

      // Maps
      { name: "Maps Test", key: "maps-test", route: "/maps-test", component: <MapsTest /> },
      { name: "Live Map", key: "live-map", route: "/live-map", component: <LiveMap /> },
    ],
  },

  { type: "title", title: "Pages", key: "title-pages" },

  // General pages
  {
    type: "collapse",
    name: "Pages",
    key: "pages",
    icon: <Icon fontSize="small">image</Icon>,
    collapse: [
      {
        name: "Projects",
        key: "projects",
        collapse: [
          { name: "Timeline", key: "timeline", route: "/pages/projects/timeline", component: <Timeline /> },
        ],
      },
      { name: "Pricing Page", key: "pricing-page", route: "/pages/pricing-page", component: <PricingPage /> },
      { name: "RTL", key: "rtl", route: "/pages/rtl", component: <RTL /> },
      { name: "Widgets", key: "widgets", route: "/pages/widgets", component: <Widgets /> },
      { name: "Charts", key: "charts", route: "/pages/charts", component: <Charts /> },
      { name: "Notfications", key: "notifications", route: "/pages/notifications", component: <Notifications /> },
    ],
  },

  // Account
  {
    type: "collapse",
    name: "Account",
    key: "account",
    icon: <Icon fontSize="small">person</Icon>,
    collapse: [
      { name: "Settings", key: "settings", route: "/pages/account/settings", component: <Settings /> },
      { name: "Billing", key: "billing", route: "/pages/account/billing", component: <Billing /> },
      { name: "Invoice", key: "invoice", route: "/pages/account/invoice", component: <Invoice /> },
    ],
  },

  // Team
  {
    type: "collapse",
    name: "Team",
    key: "team",
    icon: <Icon fontSize="small">people</Icon>,
    collapse: [
      { name: "All Projects", key: "all-projects", route: "/pages/profile/all-projects", component: <AllProjects /> },
      { name: "New User", key: "new-user", route: "/pages/users/new-user", component: <NewUser /> },
      { name: "Profile Overview", key: "profile-overview", route: "/pages/profile/profile-overview", component: <ProfileOverview /> },
    ],
  },

  // Applications (Create Task & Tasks are here)
  {
    type: "collapse",
    name: "Applications",
    key: "applications",
    icon: <Icon fontSize="small">apps</Icon>,
    collapse: [
      { name: "Create Task", key: "task-create", route: "/applications/task-create", component: <TaskCreate /> },
      { name: "Tasks", key: "task-list", route: "/applications/task-list", component: <TaskList /> },
      { name: "Kanban", key: "kanban", route: "/applications/kanban", component: <Kanban /> },
      { name: "Wizard", key: "wizard", route: "/applications/wizard", component: <Wizard /> },
      { name: "Data Tables", key: "data-tables", route: "/applications/data-tables", component: <DataTables /> },
      { name: "Calendar", key: "calendar", route: "/applications/calendar", component: <Calendar /> },
    ],
  },

  // Authentication
  {
    type: "collapse",
    name: "Authentication",
    key: "authentication",
    icon: <Icon fontSize="small">content_paste</Icon>,
    collapse: [
      {
        name: "Sign In",
        key: "sign-in",
        collapse: [
          { name: "Basic", key: "basic", route: "/authentication/sign-in/basic", component: <SignInBasic /> },
          { name: "Cover", key: "cover", route: "/authentication/sign-in/cover", component: <SignInCover /> },
          { name: "Illustration", key: "illustration", route: "/authentication/sign-in/illustration", component: <SignInIllustration /> },
        ],
      },
      {
        name: "Sign Up",
        key: "sign-up",
        collapse: [{ name: "Cover", key: "cover", route: "/authentication/sign-up/cover", component: <SignUpCover /> }],
      },
      {
        name: "Reset Password",
        key: "reset-password",
        collapse: [{ name: "Cover", key: "cover", route: "/authentication/reset-password/cover", component: <ResetCover /> }],
      },
    ],
  },

  { type: "divider", key: "divider-1" },

  // Docs (external links)
  { type: "title", title: "Docs", key: "title-docs" },
  {
    type: "collapse",
    name: "Basic",
    key: "basic",
    icon: <Icon fontSize="small">upcoming</Icon>,
    collapse: [
      {
        name: "Getting Started",
        key: "getting-started",
        collapse: [
          { name: "Overview", key: "overview", href: "https://www.justcallitin.com" },
          { name: "License", key: "license", href: "https://www.justcallitin.com" },
          { name: "Quick Start", key: "quick-start", href: "https://www.justcallitin.com" },
          { name: "Build Tools", key: "build-tools", href: "https://www.justcallitin.com" },
        ],
      },
      {
        name: "Foundation",
        key: "foundation",
        collapse: [
          { name: "Colors", key: "colors", href: "https://www.justcallitin.com" },
          { name: "Grid", key: "grid", href: "https://www.justcallitin.com" },
          { name: "Typography", key: "base-typography", href: "https://www.justcallitin.com" },
          { name: "Borders", key: "borders", href: "https://www.justcallitin.com" },
          { name: "Box Shadows", key: "box-shadows", href: "https://www.justcallitin.com" },
          { name: "Functions", key: "functions", href: "https://www.justcallitin.com" },
          { name: "Routing System", key: "routing-system", href: "https://www.justcallitin.com" },
        ],
      },
    ],
  },
  {
    type: "collapse",
    name: "Components",
    key: "components",
    icon: <Icon fontSize="small">view_in_ar</Icon>,
    collapse: [
      { name: "Alerts", key: "alerts", href: "https://www.justcallitin.com" },
      { name: "Avatar", key: "avatar", href: "https://www.justcallitin.com" },
      { name: "Badge", key: "badge", href: "https://www.justcallitin.com" },
      { name: "Badge Dot", key: "badge-dot", href: "https://www.justcallitin.com" },
      { name: "Box", key: "box", href: "https://www.justcallitin.com" },
      { name: "Buttons", key: "buttons", href: "https://www.justcallitin.com" },
      { name: "Date Picker", key: "date-picker", href: "https://www.justcallitin.com" },
      { name: "Dropzone", key: "dropzone", href: "https://www.justcallitin.com/" },
      { name: "Editor", key: "editor", href: "https://www.justcallitin.com" },
      { name: "Input", key: "input", href: "https://www.justcallitin.com" },
      { name: "Pagination", key: "pagination", href: "https://www.justcallitin.com" },
      { name: "Progress", key: "progress", href: "https://www.justcallitin.com" },
      { name: "Snackbar", key: "snackbar", href: "https://www.justcallitin.com" },
      { name: "Social Button", key: "social-button", href: "https://www.justcallitin.com" },
      { name: "Typography", key: "typography", href: "https://www.justcallitin.com" },
    ],
  },
  {
    type: "collapse",
    name: "Change Log",
    key: "changelog",
    href: "https://github.com/CallitinLLC",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    noCollapse: true,
  },
]; */

/*
 * Define the Call It In specific navigation structure. The array
 * below is used in place of the generic dashboard template routes.
 * We keep the original `routes` definition above intact in case
 * you need to refer back to it or extend it in the future. Only
 * courierRoutes is exported as the default.
 */
const courierRoutes = [
  {
    // type intentionally not "collapse" — the Sidenav renders user identity
    // as a live footer block instead of a static nav item. The collapse array
    // is still processed by App.js getRoutes() so the routes are registered.
    type: "user-profile",
    key: "user",
    collapse: [
      {
        name: "My Account",
        key: "my-account",
        route: "/pages/account/settings",
        component: <MyAccount />,
        roles: ["admin", "dispatcher", "driver"],
      },
      {
        name: "Logout",
        key: "logout",
        route: "/logout",
        component: <Logout />,
        roles: ["admin", "dispatcher", "driver"],
      },
    ],
  },
  { type: "divider", key: "divider-0" },
  {
    type: "collapse",
    name: "Dashboards",
    key: "dashboards",
    icon: <Icon fontSize="small">dashboard</Icon>,
    collapse: [
      { name: "Analytics", key: "analytics", route: "/dashboards/analytics", component: <Analytics />, roles: ["admin"] },
      { name: "Courier Dashboard", key: "courier-dashboard", route: "/dashboard/courier", component: <CourierDashboard />, roles: ["admin", "dispatcher"] },
      { name: "Driver Status", key: "driver-status", route: "/dashboards/driver-status", component: <DriverStatus />, roles: ["admin", "dispatcher"] },
      { name: "Live Map", key: "live-map", route: "/live-map", component: <LiveMap />, roles: ["admin", "dispatcher"] },
    ],
  },
  {
    type: "collapse",
    name: "Applications",
    key: "applications",
    icon: <Icon fontSize="small">apps</Icon>,
    collapse: [
      { name: "Create Task", key: "task-create", route: "/applications/task-create", component: <TaskCreate />, roles: ["admin", "dispatcher"] },
      { name: "Tasks", key: "task-list", route: "/applications/task-list", component: <TaskList />, roles: ["admin", "dispatcher"] },
      { name: "Address Book", key: "address-book", route: "/applications/address-book", component: <AddressBook />, roles: ["admin", "dispatcher"] },
      // Expose the driver interface at /driver so that drivers can bookmark
      // dispatch.justcallitin.com/driver directly.  Previously this was
      // exposed as /driver-app.
      { name: "Driver App", key: "driver-app", route: "/driver", component: <DriverApp />, roles: ["admin", "driver"] },
      { name: "Route Planner", key: "route-planner", route: "/applications/route-planner", component: <RoutePlanner />, roles: ["admin", "dispatcher"] },
    ],
  },


  // Administration — admin-only company configuration.
  // Visible only to accounts with role === "admin". Dispatchers and drivers
  // cannot see or access these routes.
  {
    type: "collapse",
    name: "Administration",
    key: "administration",
    icon: <Icon fontSize="small">tune</Icon>,
    collapse: [
      {
        name: "Administration AI",
        key: "admin-ai",
        route: "/dashboards/admin-ai",
        component: <AdminAI />,
        roles: ["admin"],
      },
      {
        name: "Company Settings",
        key: "company-settings",
        route: "/administration/company-settings",
        component: <CompanySettings />,
        roles: ["admin"],
      },
      // Moved under Administration — admin-only (was previously admin +
      // dispatcher under Applications). See main.py list_shifts /
      // get_shift_odometer_photo / list_driver_expenses /
      // get_driver_expense_receipt / review_driver_expense, which were
      // tightened from require_dispatch to require_admin to match.
      {
        name: "Driver Records",
        key: "driver-records",
        route: "/driver-records",
        component: <DriverRecords />,
        roles: ["admin"],
      },
      // Moved under Administration — admin-only (was previously admin +
      // dispatcher under Applications). See main.py get_driver_support_view /
      // log_support_view, which were tightened from require_dispatch to
      // require_admin to match.
      {
        name: "Driver Support View",
        key: "driver-support-view",
        route: "/driver-support",
        component: <DriverSupportView />,
        roles: ["admin"],
      },
      // Admin-only employee management.  Allows adding and viewing drivers, dispatchers and admins.
      {
        name: "Employees",
        key: "employees",
        route: "/employees",
        component: <Employees />,
        roles: ["admin"],
      },
    ],
  },
  // AxisSync Network — connected integration module.
  // These routes are additive; the native courier platform (Applications above)
  // operates fully without them.  Operators with AxisSync coordination duties
  // use this section to manage network-owned cases routed to Call It In for
  // courier execution.
  {
    type: "collapse",
    name: "AxisSync Network",
    key: "casesync",
    icon: <Icon fontSize="small">hub</Icon>,
    collapse: [
      { name: "Coordinate Case", key: "case-create", route: "/applications/case-create", component: <CaseCreate />, roles: ["admin"] },
      { name: "Active Cases", key: "case-list", route: "/applications/case-list", component: <CaseList />, roles: ["admin"] },
    ],
  },
  // Portal Access — external vendor/client portal user management.
  // vendor_rep accounts are NOT internal employees.  This section is intentionally
  // separate from Applications/Employees to prevent UX/security confusion.
  {
    type: "collapse",
    name: "Portal Access",
    key: "portal-access",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    collapse: [
      {
        name: "Vendor Portal Users",
        key: "vendor-portal-users",
        route: "/portal-access",
        component: <PortalAccess />,
        roles: ["admin"],
      },
    ],
  },
  {
    type: "collapse",
    name: "Authentication",
    key: "authentication",
    icon: <Icon fontSize="small">lock</Icon>,
    collapse: [
      // Login — all auth goes through the live FastAPI backend.
      { name: "Login", key: "login", route: "/login", component: <Login />, public: true, hiddenInNav: true },
    ],
  },
];

// Only export our curated courier routes. The generic template routes
// remain defined above but are not exported so they won't render in
// the application. If you need to access the original definitions
// elsewhere, import `routes` explicitly from this module.
export default courierRoutes;








